// TinyEditor.js - Complete Self-hosted version with all existing features
import React, { useRef, useEffect, useMemo, useState, forwardRef, useImperativeHandle } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { debounce } from 'lodash';
import { REPORT_CONFIG } from '../Reporting/constants';

// Import TinyMCE core
import 'tinymce/tinymce';
// Import theme
import 'tinymce/themes/silver';
// Import icons
import 'tinymce/icons/default';

// Import plugins - only import what you need
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';
import 'tinymce/plugins/pagebreak';

/**
 * Makes the TinyMCE toolbar sticky within its scroll container.
 * TinyMCE sets overflow:hidden on .tox-tinymce and .tox-editor-container,
 * which breaks position:sticky. This overrides both to visible.
 */
function makeStickyToolbar(editor) {
  const editorContainer = editor.getContainer(); // .tox-tinymce
  const toolbarHeader = editorContainer.querySelector('.tox-editor-header');
  const editorContainerInner = editorContainer.querySelector('.tox-editor-container');

  if (!toolbarHeader) return;

  editorContainer.style.overflow = 'visible';
  if (editorContainerInner) {
    editorContainerInner.style.overflow = 'visible';
  }

  toolbarHeader.style.position = 'sticky';
  toolbarHeader.style.top = '0';
  toolbarHeader.style.zIndex = '10';
}

const TinyEditor = forwardRef(({ placeholder, initialContent, handleChange, handleSaveShortCut, fromReporting, headerContent, footerContent, externalHeaderHtml, externalFooterHtml, customHeight, readOnly=false, stickyToolbar=false }, ref) => {
  const editorRef = useRef(null);
  const externalHeaderRef = useRef(null);
  const externalFooterRef = useRef(null);
  const [content, setContent] = useState(initialContent || '');
  const partialTextBookmark = useRef(null);

  useEffect(() => {
    // Set TinyMCE base URL for self-hosted setup
    if (window.tinymce) {
      // Use absolute path for both dev and production to avoid route-relative issues
      window.tinymce.baseURL = '/tinymce';
    }
  }, []);

  useEffect(() => {
    if (initialContent !== content) {
      setContent(initialContent || '');
    }
  }, [initialContent]);

  // Keep external header/footer divs in sync when their HTML changes (e.g. switching reports)
  useEffect(() => {
    if (externalHeaderRef.current) {
      externalHeaderRef.current.innerHTML = externalHeaderHtml || '';
    }
  }, [externalHeaderHtml]);

  useEffect(() => {
    if (externalFooterRef.current) {
      externalFooterRef.current.innerHTML = externalFooterHtml || '';
    }
  }, [externalFooterHtml]);

  // Clean up injected divs on unmount
  useEffect(() => {
    return () => {
      externalHeaderRef.current?.remove();
      externalHeaderRef.current = null;
      externalFooterRef.current?.remove();
      externalFooterRef.current = null;
    };
  }, []);

  const custprops = fromReporting ? {
    height: 1122, // A4 height in pixels (96 DPI)
    width: 794, // A4 width in pixels (96 DPI)
  } : customHeight ? {
    height: customHeight
  } : {
    min_height: 1122 // A4 height minimum for non-reporting mode
  }

  const config = useMemo(
    () => ({
      // Disable promotional elements
      promotion: false,

      // Remove TinyMCE branding from status bar
      branding: false,

      // Base URL for TinyMCE assets (use absolute path to avoid route-relative issues)
      base_url: '/tinymce',

      // Suffix for minified files
      suffix: '.min',

      height: custprops.height || undefined,
      min_height: custprops.min_height || 1122, // A4 minimum height
      width: custprops.width || '100%',
      menubar: 'file edit view insert format tools table help',
      contextmenu: false, // Disable custom context menu to use browser default
      quickbars_selection_toolbar: 'bold italic underline | strikethrough superscript subscript | quicklink',
      quickbars_insert_toolbar: 'quickimage media | hr | quicktable',

      plugins: [
        'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
        'searchreplace', 'code', 'fullscreen', 'insertdatetime', 'media', 'table',
        'help', 'wordcount', 'pagebreak'
      ],

      toolbar: [
        'undo redo | styles fontfamily fontsize | bold italic underline | forecolor backcolor | lineheight alignleft aligncenter alignright alignjustify',
        'bullist numlist outdent indent | link image table | cut copy paste | searchreplace removeformat | insertdatetime charmap | code pagebreak'
      ],

      // Custom font size options with labels
      // fontsize_formats: '8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 17pt 18pt 19pt 20pt 21pt 22pt 23pt 24pt 26pt 28pt 30pt 32pt 36pt 40pt 44pt 48pt 52pt 56pt 60pt 64pt 68pt 72pt',
      font_size_formats: '8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 21px 22px 23px 24px 26px 28px 30px 32px 36px 40px 44px 48px 52px 56px 60px 64px 68px 72px',

      // Custom line height options (TinyMCE 8 uses line_height_formats with underscores)
      line_height_formats: '1 1.1 1.2 1.3 1.4 1.5 1.6 1.8 2 2.5 3',

      // Ensure standard browser shortcuts are not overridden
      browser_spellcheck: true,

      // Disable enforce focus to allow keyboard shortcuts to work properly
      disable_enforce_focus: true,

      content_style: `
        body {
          font-family: Arial, sans-serif;
          font-size: 0.875rem;
          line-height: 1.6;
          margin: 0;
          padding: 1.25rem;
        }
        ${fromReporting ? `
          /* A4 page styling for reporting */
          @page {
            size: A4;
            margin: 1in;
          }

          /* Remove header/footer CSS - now handled in React components */
          /* Page container */
          body {
            background: white;
            padding: 20px;
            padding-bottom: 120px; /* Extra padding to prevent overlap with fixed footer */
          }

          /* Header and Footer styling - applies to both new and stored headers */
          .page-header-content {
            display: block;
            width: 100%;
            height: auto;
            min-height: 120px;
            background: white;
            font-size: 10px;
            margin-bottom: 10px;
            text-align: center;
          }

          .page-header-content[contenteditable="false"] {
            cursor: default;
            user-select: none;
            opacity: 0.95;
          }

          .page-footer-content {
            display: block;
            width: 100% !important;
            margin: 0 !important;
            height: auto;
            background: white !important;
            border: none;
            padding: 18px 5% !important;
            font-size: 14px;
            text-align: left;
            color: #333;
            line-height: 1.4;
            position: fixed;
            bottom: 0;
            left: 0 !important;
            box-sizing: border-box;
          }

          /* Content pages */
          .page-content {
            background: white;
            width: 8.5in;
            min-height: 11in;
            margin: 0 auto 20px;
            padding: 1in;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
            page-break-after: always;
          }

          /* Page break styling */
          .mce-pagebreak {
            page-break-before: always;
            display: block;
            border: 0;
            width: 100%;
            height: 5px;
            border-top: 1px dashed #ccc;
            margin: 20px 0;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="10"><text x="50%" y="50%" text-anchor="middle" dy="0.35em" font-size="10" fill="gray">Page Break</text></svg>') no-repeat center;
          }

          /* Auto page break styling */
          .mce-pagebreak.auto-break {
            border-top: 1px dashed #007cba;
            background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="10"><text x="50%" y="50%" text-anchor="middle" dy="0.35em" font-size="10" fill="blue">Auto Page Break</text></svg>') no-repeat center;
          }

          /* Page numbering */
          .page-number {
            position: absolute;
            bottom: 0.5in;
            right: 0.5in;
            font-size: 10px;
            color: #666;
          }

          @media print {
            body { background: white; padding: 0; }
            .page-content {
              box-shadow: none;
              margin: 0;
              width: 100%;
              min-height: auto;
            }
          }
        ` : ''}
      `,

      placeholder: placeholder || 'Start typing...',
      paste_data_images: true, // Enable image upload as Base64
      automatic_uploads: false,
      file_picker_types: 'image',

      file_picker_callback: function(callback, value, meta) {
        if (meta.filetype === 'image') {
          const input = document.createElement('input');
          input.setAttribute('type', 'file');
          input.setAttribute('accept', 'image/*');
          input.onchange = function() {
            const file = this.files[0];
            const reader = new FileReader();
            reader.onload = function() {
              callback(reader.result, { alt: file.name });
            };
            reader.readAsDataURL(file);
          };
          input.click();
        }
      },

      // Auto-pagination setup - Complete implementation from your original code
      setup: function(editor) {
        // Override keyboard shortcuts before any toolbar handling
        editor.on('keydown', function(e) {
          // Handle Ctrl+A for Select All - only select editable content, not headers/footers
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 65) {
            e.preventDefault();
            e.stopPropagation();

            editor.selection.select(editor.getBody(), true);
            return false;  // disabled skipping the header & footer from selection
          }

          // Handle Ctrl+C for Copy (let browser handle it, but prevent toolbar from intercepting)
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 67) {
            e.stopPropagation();
            return true; // Allow default browser copy
          }

          // Handle Ctrl+X for Cut (let browser handle it, but prevent toolbar from intercepting)
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 88) {
            e.stopPropagation();
            return true; // Allow default browser cut
          }

          // Handle Ctrl+V for Paste (let browser handle it, but prevent toolbar from intercepting)
          if ((e.ctrlKey || e.metaKey) && e.keyCode === 86) {
            e.stopPropagation();
            return true; // Allow default browser paste
          }
        }, true); // true = capture phase, runs before other handlers

        if (fromReporting) {
          // Add custom page break button
          editor.ui.registry.addButton('pagebreak', {
            icon: 'new-document',
            tooltip: 'Insert Page Break',
            onAction: function() {
              editor.insertContent('<div class="mce-pagebreak"></div>');
              },
            });

            editor.addCommand('customSave', () => {
              const currentContent = editor.getContent();
              setContent(currentContent);
              handleChange(currentContent);

              if (typeof handleSaveShortCut === 'function') {
                console.log('Ctrl+S save triggered');
                handleSaveShortCut('');
                editor.setDirty(false);
              }
          });
          editor.addShortcut('ctrl+s', 'Save report', 'customSave');

          // Handle Tab key press
          editor.on('keydown', function(e) {
            // Don't intercept keyboard shortcuts with Ctrl, Alt, or Meta keys
            if (e.ctrlKey || e.altKey || e.metaKey) {
              return; // Allow default behavior for keyboard shortcuts
            }

            if (e.keyCode === 9) { // Tab key
              e.preventDefault(); // Prevent default tab behavior (focus change)

              // Option 1: Insert tab spaces (4 spaces)
              editor.insertContent('&nbsp;&nbsp;&nbsp;&nbsp;');

              // Option 2: Insert actual tab character (uncomment to use this instead)
              // editor.insertContent('\t');

              // Option 3: Indent current line/selection (uncomment to use this instead)
              // editor.execCommand('Indent');
            }

            // Handle Shift+Tab for outdent
            if (e.keyCode === 9 && e.shiftKey) {
              e.preventDefault();
              editor.execCommand('Outdent');
            }
          });

          let enterPressed = false;

          editor.on('keydown', function(e) {
            if (e.keyCode === 13) { // Enter
              enterPressed = true;
            }
          });

          editor.on('keydown', function(e) {
            // If Enter was just pressed AND this is a letter key
            if (enterPressed && e.keyCode >= 65 && e.keyCode <= 90) {
              enterPressed = false;

              // Force the letter to be uppercase
              e.preventDefault();
              const upperCaseLetter = String.fromCharCode(e.keyCode);
              editor.insertContent(upperCaseLetter);
            }
            // Reset flag for any other key
            else if (enterPressed && e.keyCode !== 13) {
              enterPressed = false;
            }
          });

          // Add header/footer without pagination
          const addHeaderFooter = () => {
            try {
              const body = editor.getBody();
              if (!body) {
                console.log('Editor body not available');
                return;
              }

              // Check if header/footer already exist (from loaded report)
              const existingHeader = body.querySelector('.page-header-content');
              const existingFooter = body.querySelector('.page-footer-content');

              // Enforce editability on existing header (from loaded reports)
              if (existingHeader && REPORT_CONFIG.HEADER_NON_EDITABLE) {
                existingHeader.setAttribute('contenteditable', 'false');
              }

              // Only add header if it doesn't exist AND headerContent is provided
              if (!existingHeader && headerContent) {
                const pageHeader = document.createElement('div');
                pageHeader.className = 'page-header-content';
                pageHeader.style.cssText = `
                  display: block;
                  width: 100%;
                  height: auto;
                  min-height: 120px;
                  background: white;
                  font-size: 10px;
                  margin-bottom: 10px;
                  text-align: center;
                `;
                pageHeader.innerHTML = headerContent;
                pageHeader.setAttribute('contenteditable', REPORT_CONFIG.HEADER_NON_EDITABLE ? 'false' : 'true');

                if (body.firstChild) {
                  body.insertBefore(pageHeader, body.firstChild);
                } else {
                  body.appendChild(pageHeader);
                }
              }

              // Only add footer if it doesn't exist AND footerContent is provided
              if (!existingFooter && footerContent) {
                const pageFooter = document.createElement('div');
                pageFooter.className = 'page-footer-content';
                pageFooter.style.cssText = `
                  display: block;
                  width: 90%;
                  height: auto;
                  background: transparent;
                  border: none;
                  padding: 8px 10px;
                  font-size: 14px;
                  margin: 10px auto;
                  text-align: left;
                  color: #333;
                  line-height: 1.4;
                  position: fixed;
                  bottom: 0;
                `;
                pageFooter.innerHTML = footerContent;
                pageFooter.setAttribute('contenteditable', 'true');
                body.appendChild(pageFooter);
              }
            } catch (error) {
              console.error('Error adding header/footer:', error);
            }
          };

          // Add header/footer on init and when content changes
          editor.on('init', () => {
            setTimeout(addHeaderFooter, 100);
          });

          // Re-add header/footer when content is set (to handle content updates)
          editor.on('SetContent', () => {
            setTimeout(addHeaderFooter, 50);
          });
        }

        if (stickyToolbar) {
          editor.on('init', () => makeStickyToolbar(editor));
        }
      }
    }),
    [placeholder, fromReporting, custprops, headerContent, footerContent, customHeight, handleSaveShortCut, handleChange, stickyToolbar]
  );

  const debouncedHandleChange = useMemo(() =>
    debounce((newContent) => {
      setContent(newContent);
      handleChange(newContent);
    }, 200), [handleChange]
  );

  const handleEditorChange = (newContent) => {
    setContent(newContent);
    debouncedHandleChange(newContent);
  };

  const handleTranscriptionText = (transcribedText, isPartial = false, replacePartial = false) => {
    if (!editorRef.current) return;

    const editor = editorRef.current;

    if (replacePartial && partialTextBookmark.current) {
      // Replace existing partial text
      try {
        // Try to find the partial text span
        let partialSpan = editor.dom.get('partial-transcription-text');

        // If not found by ID, try to find by class
        if (!partialSpan) {
          const partialSpans = editor.dom.select('.partial-transcription');
          if (partialSpans.length > 0) {
            partialSpan = partialSpans[partialSpans.length - 1]; // Get the last one
          }
        }

        if (partialSpan) {
          // Select the partial span
          editor.selection.select(partialSpan);

          if (transcribedText.trim()) {
            // Replace with new text
            const formattedText = transcribedText.charAt(0).toUpperCase() + transcribedText.slice(1);
            if (isPartial) {
              // Replace with new partial text
              editor.selection.setContent(`<span id="partial-transcription-text" class="partial-transcription">${formattedText}</span>`);
            } else {
              // Replace with final text (remove partial styling)
              editor.selection.setContent(` ${formattedText} `);
              partialTextBookmark.current = null;
            }
          } else {
            // Empty text means finalize without changing content
            if (!isPartial) {
              // Remove partial styling, keep text content
              const textContent = partialSpan.textContent;
              editor.selection.setContent(` ${textContent} `);
              partialTextBookmark.current = null;
            }
          }
        } else {
          // Fallback: span not found, just insert new text
          if (transcribedText.trim()) {
            const formattedText = transcribedText.charAt(0).toUpperCase() + transcribedText.slice(1);
            editor.insertContent(` ${formattedText} `);
          }
          partialTextBookmark.current = null;
        }
      } catch (error) {
        console.error('Error replacing partial text:', error);
        // Fallback: insert new text at current position
        if (transcribedText.trim()) {
          const formattedText = transcribedText.charAt(0).toUpperCase() + transcribedText.slice(1);
          editor.insertContent(` ${formattedText} `);
        }
        partialTextBookmark.current = null;
      }
    } else if (transcribedText.trim()) {
      // Insert new text
      const formattedText = transcribedText.charAt(0).toUpperCase() + transcribedText.slice(1);

      if (isPartial) {
        // Insert as partial text with styling and create bookmark
        editor.insertContent(`<span id="partial-transcription-text" class="partial-transcription">${formattedText}</span>`);
        partialTextBookmark.current = editor.selection.getBookmark();
      } else {
        // Insert as final text
        editor.insertContent(` ${formattedText} `);
      }
    }

    // Focus back to editor
    editor.focus();
  };

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    handleTranscriptionText,
    getContent: () => {
      if (editorRef.current) {
        return editorRef.current.getContent();
      }
      return content;
    }
  }));

  return (
    <div>
      {/* Voice Transcription Toolbar - Moved to footer in ReportEditor/index.tsx */}
      {/* <div className="voice-transcribe-toolbar-container">
        <div className="voice-transcribe-info">
          <InfoCircleOutlined className="info-icon" />
          <span>Medical Dictation</span>
          <span className="medical-specialty-badge">RADIOLOGY</span>
        </div>
        <VoiceTranscribeButton
          onTranscriptionText={handleTranscriptionText}
          size="small"
          className="voice-transcribe-toolbar"
        />
      </div> */}

      <Editor
        // Remove the apiKey - not needed for self-hosting
        licenseKey='gpl'
        onInit={(evt, editor) => {
          editorRef.current = editor;

          if (!fromReporting) return;
          const editorContainer = editor.getContainer();
          if (!editorContainer) return;

          // Inject external header between toolbar and editor area
          if (externalHeaderHtml && !externalHeaderRef.current) {
            const toolbarEl = editorContainer.querySelector('.tox-editor-header');
            if (toolbarEl) {
              const headerDiv = document.createElement('div');
              headerDiv.id = 'external-report-header';
              headerDiv.innerHTML = externalHeaderHtml;
              if (REPORT_CONFIG.HEADER_NON_EDITABLE) {
                headerDiv.style.userSelect = 'none';
                headerDiv.style.pointerEvents = 'none';
                headerDiv.style.cursor = 'default';
              }
              toolbarEl.insertAdjacentElement('afterend', headerDiv);
              externalHeaderRef.current = headerDiv;
            }
          }

          // Inject external footer after the editor area (end of container)
          if (externalFooterHtml && !externalFooterRef.current) {
            const footerDiv = document.createElement('div');
            footerDiv.id = 'external-report-footer';
            footerDiv.innerHTML = externalFooterHtml;
            editorContainer.appendChild(footerDiv);
            externalFooterRef.current = footerDiv;
          }
        }}
        value={content}
        init={config}
        onBlur={(evt) => {
          const currentContent = evt.target.getContent();
          setContent(currentContent);
          handleChange(currentContent);
        }}
        onEditorChange={handleEditorChange}
        disabled={readOnly}
      />
    </div>
  );
})

export default TinyEditor;
